import {Box} from '@mui/material'
import type {FC, MouseEvent as ReactMouseEvent, ReactNode} from 'react'
import {useCallback, useEffect, useRef, useState} from 'react'
import {useResizeObserver} from '@shared/hooks/useResizeObserver'

interface Props {
  children: ReactNode
  maxWidth?: string
}

// Neutral grey works against both the light and dark card backgrounds this row appears on.
const THUMB_COLOR = 'rgba(136, 136, 136, 0.7)'
const THUMB_COLOR_ACTIVE = '#888'
const TRACK_HEIGHT = 8
const MIN_THUMB_WIDTH = 30

const HorizontalScrollingContainer: FC<Props> = ({children, maxWidth = '100%'}) => {
  const mainRef = useRef<HTMLDivElement | null>(null)
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const trackRef = useRef<HTMLDivElement | null>(null)
  const thumbRef = useRef<HTMLDivElement | null>(null)
  const [hasOverflow, setHasOverflow] = useState(false)

  // Modern Chrome/Safari on macOS render `::-webkit-scrollbar` styling as an OS-driven
  // overlay that only appears while a scroll gesture is in progress (System Settings >
  // "Show scroll bars: When scrolling"), so a purely CSS-styled native scrollbar is
  // invisible at rest regardless of author styling. Drawing our own always-visible thumb
  // (same approach as ScrollbarContainer's vertical one) sidesteps that entirely.
  const updateThumb = useCallback(() => {
    const scrollEl = scrollRef.current
    const trackEl = trackRef.current
    const thumbEl = thumbRef.current
    if (!scrollEl || !trackEl) {
      return
    }
    const {scrollLeft, scrollWidth, clientWidth} = scrollEl
    const overflow = scrollWidth > clientWidth + 1
    setHasOverflow(overflow)
    if (!overflow || !thumbEl) {
      return
    }
    const trackWidth = trackEl.clientWidth
    const thumbWidth = Math.max((clientWidth / scrollWidth) * trackWidth, MIN_THUMB_WIDTH)
    const maxScrollLeft = scrollWidth - clientWidth
    const maxThumbLeft = trackWidth - thumbWidth
    const thumbLeft = maxScrollLeft > 0 ? (scrollLeft / maxScrollLeft) * maxThumbLeft : 0
    thumbEl.style.width = `${thumbWidth}px`
    thumbEl.style.left = `${thumbLeft}px`
  }, [])

  const updateShadows = useCallback(() => {
    const mainEl = mainRef.current
    const scrollEl = scrollRef.current
    if (!mainEl || !scrollEl) {
      return
    }
    const shadowLeft = mainEl.querySelector('.shadow-left') as HTMLElement | null
    const shadowRight = mainEl.querySelector('.shadow-right') as HTMLElement | null
    if (!shadowLeft || !shadowRight) {
      return
    }
    shadowLeft.style.display = scrollEl.scrollLeft === 0 ? 'none' : 'block'
    const atEnd = Math.round(scrollEl.scrollLeft + scrollEl.clientWidth) >= scrollEl.scrollWidth
    shadowRight.style.display = atEnd ? 'none' : 'block'
  }, [])

  const handleChildResize = useCallback(() => {
    updateThumb()
    updateShadows()
  }, [updateThumb, updateShadows])
  const resizeRef = useResizeObserver(handleChildResize)

  useEffect(() => {
    const scrollEl = scrollRef.current
    if (!scrollEl) {
      return undefined
    }
    resizeRef(scrollEl)

    const handleScroll = (): void => {
      updateThumb()
      updateShadows()
    }
    scrollEl.addEventListener('scroll', handleScroll)
    updateShadows()

    return () => {
      resizeRef(null)
      scrollEl.removeEventListener('scroll', handleScroll)
    }
  }, [resizeRef, updateThumb, updateShadows])

  // Runs after every render, so once `hasOverflow` flips true and the thumb/track
  // actually mount, this re-fires and sizes the thumb - updateThumb() itself can't do
  // that on the triggering call, since the ref it needs doesn't exist until this render.
  useEffect(() => {
    updateThumb()
  })

  const handleThumbMouseDown = (event: ReactMouseEvent<HTMLDivElement>): void => {
    const scrollEl = scrollRef.current
    const trackEl = trackRef.current
    const thumbEl = thumbRef.current
    if (!scrollEl || !trackEl || !thumbEl) {
      return
    }
    event.preventDefault()
    // Results rows wrap their cards in an onClick={handleClose} (picking a result closes
    // the search popper) - without this, the native `click` fired on mouseup after a drag
    // bubbles through it and closes the whole popper mid-scroll.
    event.stopPropagation()
    const startX = event.clientX
    const startScrollLeft = scrollEl.scrollLeft
    const maxScrollLeft = scrollEl.scrollWidth - scrollEl.clientWidth
    const maxThumbLeft = trackEl.clientWidth - thumbEl.clientWidth

    const handleMouseMove = (moveEvent: globalThis.MouseEvent): void => {
      if (maxThumbLeft <= 0) {
        return
      }
      const deltaRatio = (moveEvent.clientX - startX) / maxThumbLeft
      scrollEl.scrollLeft = Math.min(
        Math.max(startScrollLeft + deltaRatio * maxScrollLeft, 0),
        maxScrollLeft,
      )
    }
    const handleMouseUp = (): void => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)

      // The track is only TRACK_HEIGHT px tall, so ordinary mouse drift during a drag
      // easily lands the mouseup off it entirely - on a card, on blank space, anywhere.
      // The native `click` that follows then never touches this component at all, and
      // bubbles straight into whatever it landed on (often a result row's
      // onClick={handleClose}), closing the whole popper mid-scroll. Swallow that one
      // click in the capture phase, wherever it lands. The 0ms timeout is a safety net
      // for the (rare, browser-dependent) case where no click follows a drag at all -
      // it runs after any same-tick click already fired, so it's a harmless no-op then,
      // and only matters to avoid leaking the listener onto some unrelated later click.
      const suppressNextClick = (clickEvent: MouseEvent): void => {
        clickEvent.stopPropagation()
        document.removeEventListener('click', suppressNextClick, true)
      }
      document.addEventListener('click', suppressNextClick, true)
      window.setTimeout(() => {
        document.removeEventListener('click', suppressNextClick, true)
      }, 0)
    }
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  const handleTrackClick = (event: ReactMouseEvent<HTMLDivElement>): void => {
    // Stop unconditionally, before the target check below: this fires for the thumb's
    // bubbled click too (track wraps it), and that click must never reach the results
    // row's onClick={handleClose} either.
    event.stopPropagation()
    const scrollEl = scrollRef.current
    const trackEl = trackRef.current
    const thumbEl = thumbRef.current
    if (!scrollEl || !trackEl || !thumbEl || event.target !== trackEl) {
      return
    }
    const trackRect = trackEl.getBoundingClientRect()
    const clickX = event.clientX - trackRect.left
    const thumbCenter = thumbEl.offsetLeft + thumbEl.clientWidth / 2
    const direction = clickX > thumbCenter ? 1 : -1
    scrollEl.scrollLeft += direction * scrollEl.clientWidth * 0.8
  }

  return (
    <Box
      sx={{
        overflow: 'hidden',
        position: 'relative',
        maxWidth,
      }}
      data-testid="mbai-main-horizontal-scrolling-container"
      ref={mainRef}
    >
      <Box
        className="shadow-left"
        sx={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          width: '10px',
          height: '100%',
          background: 'linear-gradient(90deg, rgba(0, 0, 0, 0.08), rgba(0, 0, 0, 0))',
          left: 0,
          display: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          width: '10px',
          height: '100%',
          background: 'linear-gradient(-90deg, rgba(0, 0, 0, 0.08), rgba(0, 0, 0, 0))',
          right: 0,
          display: 'none',
        }}
        className="shadow-right"
      />
      <Box
        sx={{
          overflowX: 'auto',
          display: 'flex',
          pb: `${TRACK_HEIGHT + 2}px`,
          // Hide the native scrollbar - we draw our own always-visible thumb below,
          // since OS-driven overlay scrollbars can't be forced visible via CSS alone.
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {display: 'none'},
        }}
        ref={scrollRef}
      >
        {children}
      </Box>
      <Box
        ref={trackRef}
        onClick={handleTrackClick}
        data-testid="mbai-horizontal-scrollbar-track"
        sx={{
          display: hasOverflow ? 'block' : 'none',
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: `${TRACK_HEIGHT}px`,
          cursor: 'pointer',
        }}
      >
        <Box
          ref={thumbRef}
          onMouseDown={handleThumbMouseDown}
          sx={{
            position: 'absolute',
            top: 0,
            height: `${TRACK_HEIGHT}px`,
            borderRadius: `${TRACK_HEIGHT / 2}px`,
            background: THUMB_COLOR,
            cursor: 'grab',
            '&:hover': {background: THUMB_COLOR_ACTIVE},
            '&:active': {background: THUMB_COLOR_ACTIVE, cursor: 'grabbing'},
          }}
        />
      </Box>
    </Box>
  )
}

export default HorizontalScrollingContainer
